import { debug } from "./debug";
import {
  TIMING_DEVIATION_TOLERANCE,
  TIME_ROUNDING_STEP_S,
  MIN_DURATION_S,
  NATURAL_SCALE_MIN,
  NATURAL_SCALE_MAX,
  NUDGE_MAX_ITERATIONS,
} from "./config";
import { toSeconds } from "../../utils/time";
import { compileWorkoutPlan } from "../../utils/workoutCompiler";
import { estimatePhaseSec, estimatePerRepSec } from "../../utils/repEstimator";
import { trimLiftingSets } from "./trimSets";

// --- CLASSIFICATION HELPERS -------------------------------------------------

// Mirror the compiler's recovery classification (workoutCompiler.classifyNode):
// a recovery node is Rest / Transition / Cooldown. Recovery time is a
// biological requirement, so the timing scaler must NEVER shrink it.
const isRecoveryNode = (node) => {
  const t = String(node?.type || "").toLowerCase();
  return t.includes("rest") || t === "transition" || t === "cooldown";
};

const isTimedUnit = (unit) => {
  const u = String(unit || "").toLowerCase();
  return u === "s" || u === "m";
};

const isCountedUnit = (unit) => {
  const u = String(unit || "").toLowerCase();
  return u === "reps" || u === "cal";
};

// --- LABEL-DURATION PARSING (issue #63) --------------------------------------
//
// A time-capped Group (AMRAP / "for time" cap) whose LABEL states a duration
// ("15-Minute AMRAP", "12 Min AMRAP") makes a promise to the user. The LLM
// sometimes emits a cap that contradicts its own label (label "15-Minute",
// value 23.7 — issue #63), and the timing scaler would otherwise drift a
// named cap away from its label. So the label is authoritative: at
// normalisation the cap is snapped to the label-stated duration, and the
// scaler later treats such Groups as fixed time. Stateless — pinned state is
// re-derived from the label each time, so no internal flags leak into saved
// plans or exported YAML.

const LABEL_DURATION_RE =
  /(\d+(?:\.\d+)?)\s*[-–\s]\s*(minutes?|mins?|seconds?|secs?)\b/i;

const labelDurationSeconds = (label) => {
  const m = LABEL_DURATION_RE.exec(String(label || ""));
  if (!m) return null;
  const value = Number(m[1]);
  if (!Number.isFinite(value) || value <= 0) return null;
  return /^s/i.test(m[2]) ? value : value * 60;
};

// Snap a time-capped Group's value to the duration stated in its own label
// (issue #63). The label is the user-facing contract; the cap drives the
// runtime, so the cap is made to match.
const pinLabelDuration = (node) => {
  if (node?.type !== "Group" || !isTimedUnit(node.unit)) return;
  const seconds = labelDurationSeconds(node.label);
  if (seconds == null) return;
  const currentSeconds = toSeconds(node.value, node.unit);
  if (Math.abs(currentSeconds - seconds) < 1) return; // already consistent
  debug.warn(
    `Pinned "${node.label}" cap to its label duration: ${currentSeconds}s → ${seconds}s`,
  );
  if (String(node.unit).toLowerCase() === "m") {
    node.value = Math.round((seconds / 60) * 10) / 10; // 1 decimal for minutes
  } else {
    node.value = Math.round(seconds);
  }
};

// Counts are integers by schema (docs/schema.md). Round fractional values the
// LLM emitted ("23.7 rounds" was the visible symptom in issue #63) rather
// than letting them propagate into loop counters and time estimates.
const COUNT_UNITS = ["rounds", "sets", "workout"];

const roundFractionalCounts = (node) => {
  if (!node || typeof node !== "object") return;
  const unit = String(node.unit || "").toLowerCase();
  const isCount =
    (node.type === "Group" && COUNT_UNITS.includes(unit)) || isCountedUnit(unit);
  if (!isCount) return;
  const value = Number(node.value);
  if (!Number.isFinite(value) || Number.isInteger(value)) return;
  const rounded = Math.max(1, Math.round(value));
  debug.warn(
    `Rounded fractional count on "${node.label || node.id}": ${value} → ${rounded} ${unit}`,
  );
  node.value = rounded;
};

// --- ERG DISTANCE REINTERPRETATION ------------------------------------------
//
// The schema has no distance unit (only s / m / reps — see docs/schema.md), so
// a distance target on an ergo collides with "m" = minutes: a 250 m row the
// LLM emits as { value: 250, unit: "m" } is read as 250 MINUTES (15,000 s),
// which then dominates the whole-session estimate and forces a catastrophic
// downscale of everything else.
//
// A single continuous interval is never tens of minutes long, and nobody
// prescribes an ergo piece as "<100 metres". So when a Work node names a
// distance-capable ergo AND its "minutes" value is >= DISTANCE_MIN_METRES, it
// is really metres — reinterpret it as an estimated duration from a rough pace.
// A legit long time piece ("40 min row", value 40) stays minutes.
const DISTANCE_MIN_METRES = 100;

// Ordered; first label match wins. Seconds per metre at a sustainable pace.
const ERG_PACE = [
  { re: /(bike\s*erg|assault|echo|air\s*bike|watt\s*bike|\bbike\b)/, secPerM: 0.08 },
  { re: /(ski\s*erg|skierg|\bski\b)/, secPerM: 0.25 },
  { re: /(row|rower|\berg\b|concept\s*2|\bc2\b)/, secPerM: 0.24 },
  { re: /(run|jog|sprint|shuttle)/, secPerM: 0.3 },
  { re: /(swim)/, secPerM: 1.2 },
];

const reinterpretErgDistance = (node) => {
  if (!node || node.type !== "Work") return;
  if (String(node.unit || "").toLowerCase() !== "m") return;
  const value = Number(node.value);
  if (!Number.isFinite(value) || value < DISTANCE_MIN_METRES) return;
  const label = String(node.label || "").toLowerCase();
  const hit = ERG_PACE.find((e) => e.re.test(label));
  if (!hit) return; // not a recognised ergo — leave for the absurd-value clamp

  const seconds = Math.max(MIN_DURATION_S, Math.round(value * hit.secPerM));
  debug.warn(
    `Reinterpreted "${node.label}" as ${value} m → ~${seconds}s (was read as ${value} min)`,
  );
  node.value = seconds;
  node.unit = "s";
  node.estimatedFromDistanceM = value; // provenance for the UI / debugging
};

// Backstop: no single timed interval is longer than an hour. Catches any
// remaining absurd emission (a mistyped value, a non-ergo distance) so one node
// can never poison the whole-session estimate again.
const SANE_MAX_LEAF_S = 3600;

const clampAbsurdTimed = (node) => {
  if (!isTimedUnit(node?.unit)) return;
  const secs = toSeconds(node.value, node.unit);
  if (secs <= SANE_MAX_LEAF_S) return;
  debug.warn(
    `Clamping "${node.label || node.id}" from ${secs}s to ${SANE_MAX_LEAF_S}s (implausible single interval)`,
  );
  if (String(node.unit).toLowerCase() === "m") node.value = SANE_MAX_LEAF_S / 60;
  else node.value = SANE_MAX_LEAF_S;
  node.clampedFromS = secs;
};

// --- POST-LLM TIME VALIDATION & ADJUSTMENT ---

// Scale a timed node's value in place by `factor`, honouring the rounding step,
// the floor, and the node's own unit.
const scaleTimedValue = (node, factor) => {
  const currentSeconds = toSeconds(node.value, node.unit);
  let newSeconds =
    Math.round((currentSeconds * factor) / TIME_ROUNDING_STEP_S) *
    TIME_ROUNDING_STEP_S;
  newSeconds = Math.max(MIN_DURATION_S, newSeconds);
  if (String(node.unit).toLowerCase() === "m") {
    node.value = Math.round((newSeconds / 60) * 10) / 10; // 1 decimal for minutes
  } else {
    node.value = newSeconds;
  }
};

// Bound the scale factor so a single bad estimate can never destroy the plan:
// a physiologically valid session slightly off target beats an on-target one
// with 5-second work intervals. The erg/clamp fixes above keep the raw estimate
// sane, so in practice the factor stays well inside these bounds.
const MIN_SCALE_FACTOR = 0.25;
const MAX_SCALE_FACTOR = 4;

// --- ROUND NUDGING (issue #63) ------------------------------------------------
//
// Time scaling stretches/shrinks every interval by the same factor. Past a
// point that is unnatural (a 1:20 assault-bike effort scaled to 3:28 is a
// different session). When the required factor leaves the natural band
// (NATURAL_SCALE_MIN/MAX), first adjust integer ROUND COUNTS on interval-style
// groups — adding a round preserves the per-interval character — then scale
// only the residual. Greedy best-first: each move is ±1 round, applied only
// if it strictly reduces the deviation from target.
const NUDGE_MAX_ADD_FACTOR = 2; // never more than double the original rounds
const NUDGE_MAX_REMOVE = 1; // never remove more than one round

// Fixed-protocol blocks. The EMOM family is matched with its window prefix
// (EMOM / E2MOM / E3MOM…) so a block relabelled by enforceEmomWindows keeps its
// protection — a bare /emom/ does NOT match "E4MOM".
const PROTOCOL_LABEL_RE = /tabata|\be\d*mom\b/i;
const WARM_COOL_RE = /warm[\s-]?up|cool[\s-]?down/i;

// A nudge candidate is a TOP-LEVEL, interval-style count Group:
//   (a) contains at least one scalable active timed Work leaf (the content the
//       scaler would otherwise distort) — reps-only strength groups are lifting
//       prescriptions, not time filler;
//   (b) is not itself pinned (label-stated duration) or protocol-labelled, and
//       contains no pinned/protocol sub-block (a nudge would multiply it);
//   (c) carries no setScheme anywhere below it (per-set arrays are
//       length-locked to the round count — issue #29);
//   (d) is not a warm-up / cool-down section.
// Groups nested inside a multi-round parent are excluded by the top-level
// rule: changing their value would multiply the delta through the parent.
const isNudgeCandidate = (node) => {
  if (!node || node.type !== "Group") return false;
  const unit = String(node.unit || "").toLowerCase();
  if (unit !== "rounds" && unit !== "sets") return false;
  if (
    WARM_COOL_RE.test(String(node.label || "")) ||
    WARM_COOL_RE.test(String(node.type || ""))
  ) {
    return false;
  }

  let hasScalableTimedWork = false;
  let disqualified = false;
  const visit = (n) => {
    if (!n || disqualified) return;
    if (n.setScheme != null) {
      disqualified = true;
      return;
    }
    if (n.type === "Group") {
      const label = String(n.label || "");
      if (labelDurationSeconds(label) != null || PROTOCOL_LABEL_RE.test(label)) {
        disqualified = true;
        return;
      }
    }
    if (n.type === "Work" && isTimedUnit(n.unit) && !isRecoveryNode(n)) {
      hasScalableTimedWork = true;
    }
    if (Array.isArray(n.children)) n.children.forEach(visit);
  };
  visit(node);
  return !disqualified && hasScalableTimedWork;
};

// Greedy best-first round-count adjustment. Returns the number of nudges
// applied. Each candidate's delta is measured by compiling the WHOLE plan with
// the candidate temporarily set to the candidate value — exact by construction
// (rest-filter interactions included) and cheap on these small trees.
const nudgeRoundCounts = (plan, targetSeconds, skipLastRest) => {
  if (!Array.isArray(plan?.children)) return 0;
  const candidates = plan.children.filter(isNudgeCandidate);
  if (candidates.length === 0) return 0;

  const totalWithValue = (group, value) => {
    const saved = group.value;
    group.value = value;
    const total = compileWorkoutPlan(plan, { skipLastRest }).totalDuration;
    group.value = saved;
    return total;
  };

  // Baseline counts so the per-group bounds survive multiple iterations.
  const original = new Map(candidates.map((g) => [g, Number(g.value) || 1]));

  let total = compileWorkoutPlan(plan, { skipLastRest }).totalDuration;
  let deviation = Math.abs(total - targetSeconds);
  let nudges = 0;

  for (let i = 0; i < NUDGE_MAX_ITERATIONS; i++) {
    if (deviation / targetSeconds <= TIMING_DEVIATION_TOLERANCE) break;
    let best = null;

    for (const group of candidates) {
      const orig = original.get(group);
      const current = Number(group.value) || 1;
      const lo = Math.max(1, orig - NUDGE_MAX_REMOVE);
      const hi = orig * NUDGE_MAX_ADD_FACTOR;
      for (const next of [current - 1, current + 1]) {
        if (next < lo || next > hi || next === current) continue;
        const resultingTotal = totalWithValue(group, next);
        const resulting = Math.abs(resultingTotal - targetSeconds);
        if (resulting < deviation && (!best || resulting < best.deviation)) {
          best = { group, next, total: resultingTotal, deviation: resulting };
        }
      }
    }

    if (!best) break;
    best.group.value = best.next;
    total = best.total;
    deviation = best.deviation;
    nudges += 1;
    debug.warn(
      `Nudged "${best.group.label || best.group.id}" to ${best.next} rounds ` +
        `(session now ${(total / 60).toFixed(1)} min vs ${(targetSeconds / 60).toFixed(1)} min target)`,
    );
  }
  return nudges;
};

const validateAndAdjustTiming = (
  plan,
  timeAvailableMinutes,
  { skipLastRest = true, onRepair } = {},
) => {
  const targetSeconds = timeAvailableMinutes * 60;

  // Lifting overrun (#101): reps and heavy-set rest are never scaled below, so
  // an overlong lifting session would stay overlong. Trim accessory sets first.
  const trim = trimLiftingSets(
    plan,
    targetSeconds,
    (p) => compileWorkoutPlan(p, { skipLastRest }).totalDuration,
  );
  if (trim.setsRemoved || trim.exercisesRemoved.length) {
    if (typeof onRepair === "function") onRepair("repair.liftingTrim");
    debug.warn(
      `Lifting overrun: ${(trim.before / 60).toFixed(1)} -> ${(trim.after / 60).toFixed(1)} min, ` +
        `${trim.setsRemoved} accessory set(s) removed` +
        (trim.exercisesRemoved.length ? `, dropped ${trim.exercisesRemoved.join(", ")}` : ""),
    );
  }

  // Compile once: gives both the total AND the per-item kind/isTimed split we
  // need to scale ACTIVE work only (rounds handled correctly — a node repeated
  // across rounds contributes proportionally, so scaling its value scales its
  // whole contribution).
  const { queue, totalDuration: actualSeconds } = compileWorkoutPlan(plan, {
    skipLastRest,
  });

  debug.log(
    `Time validation: target=${targetSeconds}s (${timeAvailableMinutes}min), actual=${actualSeconds}s (${(actualSeconds / 60).toFixed(1)}min)`,
  );

  if (actualSeconds === 0) {
    debug.warn(
      "Total duration is 0 — session may be entirely reps-based, skipping adjustment",
    );
    return;
  }

  const deviation = Math.abs(actualSeconds - targetSeconds) / targetSeconds;
  if (deviation <= TIMING_DEVIATION_TOLERANCE) {
    debug.log(
      `Duration within ${TIMING_DEVIATION_TOLERANCE * 100}% tolerance (${(deviation * 100).toFixed(1)}%), no adjustment needed`,
    );
    return;
  }

  // --- Protection sets (issue #63) ------------------------------------------
  // Pinned caps: time-capped Groups whose label states a duration — the label
  // is authoritative (see pinLabelDuration), so the cap is fixed time.
  // Protocol intervals: timed Work leaves inside a Tabata-labelled Group — a
  // scaled Tabata (26s/10s) is no longer a Tabata, so those are fixed too.
  const protectedLeaves = new Set();
  const collectProtected = (node, insideProtocol) => {
    if (!node || typeof node !== "object") return;
    const protocol =
      insideProtocol ||
      (node.type === "Group" && PROTOCOL_LABEL_RE.test(String(node.label || "")));
    if (protocol && node.type === "Work" && isTimedUnit(node.unit)) {
      protectedLeaves.add(node);
    }
    // An EMOM's window container is a time-capped Group with no duration in its
    // own label, so the pinned-cap rule below would miss it. Scaling it (2:00 →
    // 2:53) destroys the format outright (issue #63).
    if (protocol && node.type === "Group" && isTimedUnit(node.unit)) {
      protectedLeaves.add(node);
    }
    if (Array.isArray(node.children)) {
      node.children.forEach((c) => collectProtected(c, protocol));
    }
  };
  collectProtected(plan, false);

  // Only ACTIVE timed content is scalable. Recovery (rest/transition/cooldown),
  // rep-based estimates, pinned caps, and protocol intervals are held fixed —
  // shrinking rest between heavy sets or rewriting a named/protocol block
  // makes the programme physiologically invalid (issue #63). Excluded items
  // fall into fixedSecs, so the factor targets (target − fixed) and the budget
  // correctly accounts for the excluded blocks.
  const isScalableItem = (item) =>
    item.isTimed &&
    item.kind === "active" &&
    !(
      item.node?.type === "Group" &&
      labelDurationSeconds(item.node.label) != null
    ) &&
    !protectedLeaves.has(item.node);

  let activeTimedSecs = queue.reduce(
    (sum, item) => sum + (isScalableItem(item) ? item.durationSec : 0),
    0,
  );
  if (activeTimedSecs <= 0) {
    debug.warn(
      "No scalable active timed content — leaving rest/reps intact, no adjustment",
    );
    return;
  }

  let fixedSecs = actualSeconds - activeTimedSecs; // rest + reps + pinned + protocol
  let rawFactor = (targetSeconds - fixedSecs) / activeTimedSecs;

  // --- Round nudging (issue #63) --------------------------------------------
  // When the required factor would produce unnatural intervals (a 1:20 effort
  // scaled to 3:28), first adjust the ROUND COUNTS of interval-style groups —
  // an extra round preserves the per-interval character that scaling would
  // destroy. Scaling then only has to absorb the residual.
  if (rawFactor < NATURAL_SCALE_MIN || rawFactor > NATURAL_SCALE_MAX) {
    const nudges = nudgeRoundCounts(plan, targetSeconds, skipLastRest);
    if (nudges > 0) {
      const recompiled = compileWorkoutPlan(plan, { skipLastRest });
      const nudgedTotal = recompiled.totalDuration;
      if (
        Math.abs(nudgedTotal - targetSeconds) / targetSeconds <=
        TIMING_DEVIATION_TOLERANCE
      ) {
        debug.log(
          `Round nudging closed the gap (${(nudgedTotal / 60).toFixed(1)} min) — no scaling needed`,
        );
        return;
      }
      activeTimedSecs = recompiled.queue.reduce(
        (sum, item) => sum + (isScalableItem(item) ? item.durationSec : 0),
        0,
      );
      if (activeTimedSecs <= 0) {
        debug.warn("No scalable content left after nudging — stopping");
        return;
      }
      fixedSecs = nudgedTotal - activeTimedSecs;
      rawFactor = (targetSeconds - fixedSecs) / activeTimedSecs;
    }
  }

  const targetActive = targetSeconds - fixedSecs;
  if (targetActive <= MIN_DURATION_S) {
    // Fixed recovery/reps alone meet or exceed the target. Scaling active work
    // to zero would just floor it to 5s each; better to leave the plan valid
    // and let validatePlan surface the residual for the refinement pass.
    debug.warn(
      `Fixed recovery/reps (${fixedSecs}s) already near/over target ${targetSeconds}s — leaving active work intact`,
    );
    return;
  }

  const scaleFactor = Math.min(
    Math.max(rawFactor, MIN_SCALE_FACTOR),
    MAX_SCALE_FACTOR,
  );
  const residualDeviation =
    Math.abs(fixedSecs + activeTimedSecs - targetSeconds) / targetSeconds;
  debug.warn(
    `Duration off by ${(residualDeviation * 100).toFixed(1)}% — scaling ACTIVE timed nodes by ${scaleFactor.toFixed(3)} (rest, pinned caps, and protocol intervals held fixed)`,
  );

  // Walk the tree and scale active timed content in place.
  const scaleNode = (node) => {
    if (!node) return;

    // Time-capped Group (AMRAP / "for time" cap): the value is the duration
    // cap, scale it and do NOT recurse (children are a checklist, not phases).
    // Pinned caps (label-stated duration, issue #63) are fixed — never scaled.
    if (node.type === "Group" && isTimedUnit(node.unit)) {
      if (
        !isRecoveryNode(node) &&
        labelDurationSeconds(node.label) == null &&
        !protectedLeaves.has(node) // EMOM window — fixed by the protocol
      ) {
        scaleTimedValue(node, scaleFactor);
      }
      return;
    }

    if (!node.children || node.children.length === 0) {
      // Leaf — scale only active timed nodes (never rest/transition/cooldown,
      // never protocol intervals such as a Tabata's 20s work — issue #63).
      if (
        isTimedUnit(node.unit) &&
        !isRecoveryNode(node) &&
        !protectedLeaves.has(node)
      ) {
        scaleTimedValue(node, scaleFactor);
      }
      return;
    }

    node.children.forEach(scaleNode);
  };

  scaleNode(plan);

  const adjustedSeconds = calculateAdjustedTotal(plan, skipLastRest);
  debug.log(
    `After adjustment: ${adjustedSeconds}s (${(adjustedSeconds / 60).toFixed(1)}min)`,
  );
};

// Local helper so the post-adjust log reflects the same rest-filter setting.
const calculateAdjustedTotal = (plan, skipLastRest) =>
  compileWorkoutPlan(plan, { skipLastRest }).totalDuration;

// --- POST-LLM UNIT NORMALIZATION ---

const UNIT_MAP = {
  second: "s",
  seconds: "s",
  sec: "s",
  secs: "s",
  minute: "m",
  minutes: "m",
  min: "m",
  mins: "m",
  // Calorie targets on ergo machines. Models write this half a dozen ways, and
  // an uncanonical spelling fails the VALID_UNITS gate and is treated as an
  // untimed unknown — the same silent-failure class as verbose "seconds".
  cals: "cal",
  calorie: "cal",
  calories: "cal",
  kcal: "cal",
  kcals: "cal",
};

const normalizeUnits = (node) => {
  if (!node || typeof node !== "object") return node;
  if (node.unit && typeof node.unit === "string") {
    const normalized = UNIT_MAP[node.unit.toLowerCase()];
    if (normalized) node.unit = normalized;
  }

  // Collapse the legacy `SetRest` type into the unified `Rest`. Runtime treats
  // both identically; round-boundary semantics are recovered structurally
  // (last child of a multi-round Group). See planNormaliser.js.
  if (node.type === "SetRest") node.type = "Rest";

  // Ergo distance mis-emission ("250 m" row read as 250 minutes), then a
  // backstop clamp for any remaining absurd single-interval duration. Order
  // matters: reinterpret first (may turn "m" into a small "s"), then clamp.
  reinterpretErgDistance(node);
  clampAbsurdTimed(node);

  // Label-duration pinning (issue #63): a time-capped Group whose label states
  // a duration gets its cap snapped to the label, and fractional counts are
  // rounded to integers per the schema.
  pinLabelDuration(node);
  roundFractionalCounts(node);

  // Coerce optional VBT load fields on Work nodes. Strip anything malformed
  // so downstream code can trust `Number.isFinite(node.weight)` etc.
  if (node.weight != null) {
    const w = Number(node.weight);
    if (Number.isFinite(w) && w > 0) node.weight = w;
    else delete node.weight;
  }
  if (node.intensityPct != null) {
    const p = Number(node.intensityPct);
    if (Number.isFinite(p) && p > 0) {
      node.intensityPct = Math.min(100, p);
    } else {
      delete node.intensityPct;
    }
  }

  // The same rule for the per-set arrays. A model told to "skip load for
  // bodyweight" will still fill every setScheme dimension to the set count,
  // which lands weight: [0,0,0] on a hanging leg raise — the absence of a
  // load written out as a load of zero. Left in the plan it is not merely
  // untidy: it exports into the shared YAML, it counts as a load in the
  // display fallback chain (hiding the RPE the exercise carries), and a
  // setScheme of any kind disqualifies its Group from duration nudging
  // (isNudgeCandidate), so a bodyweight circuit silently stops being
  // scalable. A ramp that merely starts at zero is real and stays.
  if (node.setScheme && typeof node.setScheme === "object") {
    for (const dim of ["weight", "intensityPct"]) {
      const arr = node.setScheme[dim];
      if (!Array.isArray(arr)) continue;
      if (!arr.some((v) => Number.isFinite(Number(v)) && Number(v) > 0)) {
        delete node.setScheme[dim];
      }
    }

    // A dimension whose every entry is the SAME number is not a ramp — it is
    // the static field written out N times. The prompt says so ("only include
    // dimensions that actually change"; "for IDENTICAL sets, omit setScheme
    // entirely") and models still emit `[10,10,10]` / `[80,80,80]`. The
    // normaliser's own folding pass (buildSetGroup) has always applied this
    // rule when it BUILDS a sets-Group — "emitted only when those values vary,
    // a uniform dimension stays on the static set-1 field". Generation never
    // learned it. Same split as the zero-fill above: the single-value path
    // knowing something the per-set path does not.
    //
    // It is not cosmetic here either. Beyond printing "10/10/10x · 80/80/80 kg",
    // a setScheme of ANY kind disqualifies its Group from duration nudging
    // (isNudgeCandidate), so meaningless uniform arrays silently cost the plan
    // the one repair that can add volume to an under-filled session.
    for (const dim of ["value", "weight", "intensityPct"]) {
      const arr = node.setScheme[dim];
      if (!Array.isArray(arr) || arr.length === 0) continue;
      const first = Number(arr[0]);
      if (!Number.isFinite(first)) continue;
      if (!arr.every((v) => Number(v) === first)) continue;
      // Set 1 IS the prescription, so promote it to the static field before
      // dropping the array — the model is meant to mirror set 1 there already,
      // but a plan that disagrees with itself must resolve in favour of the
      // per-set values it actually authored. Only on a Work node: `value` on a
      // Group is its round count, and overwriting that with a rep count would
      // rewrite the session.
      if (node.type === "Work") {
        node[dim] = dim === "intensityPct" ? Math.min(100, first) : first;
      }
      delete node.setScheme[dim];
    }

    if (Object.keys(node.setScheme).length === 0) delete node.setScheme;
  }

  if (Array.isArray(node.children)) {
    node.children.forEach(normalizeUnits);
  }
  return node;
};

// --- EMOM WINDOW ENFORCEMENT (issue #63) --------------------------------------
//
// EMOM is a WINDOW format: the work starts at the top of a fixed window and
// whatever is left of that window is the rest, so the next window always starts
// on the clock. EMOM is a 60s window; E2MOM / E3MOM / E4MOM are the identical
// mechanic over 2 / 3 / 4 minutes, used when the round holds more volume than a
// minute can carry. The schema has no window primitive, so the format is
// encoded as Group(unit:"rounds", value: N windows) whose children are the
// round's stations followed by ONE Rest sized to the residual.
//
// Models emit all three numbers in disagreement. The block that reopened this
// issue was labelled "12-Min EMOM" but ran 3 rounds of four movements plus an
// arbitrary 30s rest: a 60-second window holding two minutes of work, and a
// label promising four times the duration the structure delivers.
//
// Reconciled deterministically, in this order of authority:
//   1. the total duration stated in the label (the promise to the user),
//   2. the round count (prescribed volume — an extra round is a real dose),
//   3. the EMOM/E2MOM token (the least reliable figure a model writes).
// A SINGLE-station block inverts 2 and 3: "12-Min EMOM, 8 pull-ups" can only
// mean twelve one-minute windows, so the token is the window and the round
// count is derived.
//
// The window itself is emitted as a TIME-CAPPED GROUP — the same container
// AMRAP uses (issue #36): the compiler runs it as one countdown and treats its
// children as a checklist rather than individual phases. That is the format's
// actual mechanic. An EMOM therefore carries NO Rest node: prescribing one
// ("rest 1:20") is a fiction that only holds if the athlete moves at exactly
// the estimated pace, and it denies the whole point — finish faster, rest
// longer. The leftover of the countdown IS the rest.
//
//   Group(unit:"rounds", value:N)          ← N windows
//     └── Group(unit:"s", value:window)    ← one self-paced countdown
//           └── the stations, as counted work
const EMOM_WINDOW_S = 60;
const EMOM_MAX_WINDOW_S = 600; // E10MOM — beyond this it isn't the format
const EMOM_MAX_ROUNDS = 60;
const EMOM_FIT_ITERATIONS = 5;

const EMOM_TOKEN_RE = /\be(\d*)mom\b/i;

// Window stated by the label's own format token: EMOM → 60s, E3MOM → 180s.
const emomTokenWindowS = (label) => {
  const m = EMOM_TOKEN_RE.exec(String(label || ""));
  if (!m) return null;
  const n = m[1] ? Number(m[1]) : 1;
  if (!Number.isFinite(n) || n <= 0) return EMOM_WINDOW_S;
  return Math.min(n * EMOM_WINDOW_S, EMOM_MAX_WINDOW_S);
};

// Seconds a station consumes inside its window, via the same estimator the
// compiler uses for session totals — so the block's arithmetic and the
// session's agree.
const stationSeconds = (node) => {
  if (!node) return 0;
  if (isTimedUnit(node.unit)) return toSeconds(node.value, node.unit);
  // Counted work (reps or calories) resolves through the same estimator the
  // compiler uses for session totals — so the block's arithmetic and the
  // session's agree. A calorie station reading as 0 here would let the window
  // density check treat a 20 cal bike sprint as free.
  if (isCountedUnit(node.unit)) {
    return estimatePhaseSec(node, null, null);
  }
  return 0;
};

const clampInt = (value, lo, hi) =>
  Math.min(hi, Math.max(lo, Math.round(value)));

// --- Counted work ------------------------------------------------------------
//
// EMOM work must be COUNTED, not timed. A 50s timed station inside a 60s window
// leaves nothing to earn by moving faster — that is a circuit wearing an EMOM's
// name. So a timed station is rewritten as the work it represents:
//   • an ergo becomes a distance target (the schema has no distance unit, so it
//     keeps seconds and carries estimatedFromDistanceM — the same provenance
//     shape reinterpretErgDistance already writes, which the UI renders);
//   • an isometric hold or loaded carry stays timed (reps are meaningless for a
//     plank, and the hold IS the prescription);
//   • everything else becomes reps, inverting the per-rep estimator.
const ISOMETRIC_RE = /plank|hold|hang|carry|wall\s*sit|bridge|iso\b/i;

// Distance rounding per modality — a rower is prescribed to 50 m, not to 208 m.
const ERG_DISTANCE_STEP_M = [
  { re: /(run|jog|sprint|shuttle)/, step: 100 },
  { re: /(swim)/, step: 25 },
];
const DEFAULT_ERG_STEP_M = 50;

const ergDistanceStep = (label) =>
  ERG_DISTANCE_STEP_M.find((e) => e.re.test(label))?.step ?? DEFAULT_ERG_STEP_M;

// Rewrite one timed station as counted work, in place. No-op for a station that
// is already counted, or one that should stay timed.
const countifyStation = (node) => {
  if (!node || !isTimedUnit(node.unit)) return;
  const seconds = toSeconds(node.value, node.unit);
  if (!Number.isFinite(seconds) || seconds <= 0) return;
  const label = String(node.label || "").toLowerCase();

  if (ISOMETRIC_RE.test(label)) return; // the hold is the prescription

  const erg = ERG_PACE.find((e) => e.re.test(label));
  if (erg) {
    const step = ergDistanceStep(label);
    const metres = Math.max(step, Math.round(seconds / erg.secPerM / step) * step);
    node.value = Math.max(MIN_DURATION_S, Math.round(metres * erg.secPerM));
    node.unit = "s";
    node.estimatedFromDistanceM = metres;
    debug.warn(
      `EMOM station "${node.label}": ${seconds}s → ${metres} m (counted work)`,
    );
    return;
  }

  const { perRepSec } = estimatePerRepSec(node, null, null);
  if (!Number.isFinite(perRepSec) || perRepSec <= 0) return;
  const reps = Math.max(1, Math.round(seconds / perRepSec));
  node.value = reps;
  node.unit = "reps";
  node.convertedFromSeconds = seconds; // provenance for the UI / debugging
  debug.warn(
    `EMOM station "${node.label}": ${seconds}s → ${reps} reps (counted work)`,
  );
};

// --- Window density ----------------------------------------------------------
//
// The work should fill a decent share of its window; ~40s of work in a 2-minute
// window is a third work and two-thirds rest, which is not the stimulus an EMOM
// is for. Below DENSITY_MIN the window is tightened to the smallest whole
// minute that keeps the work under DENSITY_MAX.
//
// Genuinely heavy work is exempt: for a near-maximal triple the long window IS
// the prescription (CNS recovery), so an E3MOM of 90% singles keeps its three
// minutes. Judged on explicit load only — a bare "75% 1RM" bench is not heavy
// enough to justify two-thirds idle.
const DENSITY_MIN = 0.4;
const DENSITY_MAX = 0.85;
const HEAVY_INTENSITY_PCT = 80;

const isHeavyStation = (node) => {
  const pct = Number(node?.intensityPct);
  if (Number.isFinite(pct) && pct >= HEAVY_INTENSITY_PCT) return true;
  const cue = String(node?.intensityTarget || "");
  if (/heavy|max(imal)?\s*effort|near[\s-]?max/i.test(cue)) return true;
  const rpe = /rpe\s*(\d+(?:\.\d+)?)/i.exec(cue);
  if (rpe && Number(rpe[1]) >= 8) return true;
  const oneRm = /(\d+(?:\.\d+)?)\s*%\s*(?:of\s*)?1\s*rm/i.exec(cue);
  if (oneRm && Number(oneRm[1]) >= HEAVY_INTENSITY_PCT) return true;
  return false;
};

// Shrink the stations proportionally so the round's work fits `budget`
// seconds. Reps floor at 1, timed work at MIN_DURATION_S on the rounding step.
// A station carrying a setScheme is left alone — its per-set arrays are
// length-locked to the round count (issue #29). Returns true if anything moved.
const trimStationsToFit = (stations, budget) => {
  const trimmable = stations.filter((s) => s.setScheme == null);
  const trimmableSecs = trimmable.reduce((sum, s) => sum + stationSeconds(s), 0);
  if (trimmableSecs <= 0) return false;
  const fixedSecs = stations.reduce(
    (sum, s) => sum + (s.setScheme == null ? 0 : stationSeconds(s)),
    0,
  );
  const factor = (budget - fixedSecs) / trimmableSecs;
  if (!Number.isFinite(factor) || factor <= 0 || factor >= 1) return false;

  let changed = false;
  for (const station of trimmable) {
    const before = station.value;
    if (isTimedUnit(station.unit)) {
      scaleTimedValue(station, factor);
    } else if (isCountedUnit(station.unit)) {
      station.value = Math.max(1, Math.floor(Number(station.value) * factor));
    }
    if (station.value !== before) {
      changed = true;
      debug.warn(
        `EMOM station "${station.label || station.id}" trimmed to fit its window: ${before} → ${station.value} ${station.unit}`,
      );
    }
  }
  return changed;
};

const emomToken = (windowS) =>
  windowS === EMOM_WINDOW_S ? "EMOM" : `E${windowS / EMOM_WINDOW_S}MOM`;

// Rewrite the label so it states the block's real duration and window, leaving
// any descriptive remainder ("EMOM Block" → "5-Min EMOM Block") intact. A label
// that already agrees is returned unchanged.
const emomLabel = (label, windowS, totalS) => {
  const token = emomToken(windowS);
  const minutes = Math.round(totalS / 60);
  let next = String(label || "").replace(EMOM_TOKEN_RE, token);
  const stated = labelDurationSeconds(next);
  if (stated == null) {
    next = `${minutes}-Min ${next}`.trim();
  } else if (Math.abs(stated - totalS) >= 1) {
    next = next.replace(LABEL_DURATION_RE, `${minutes}-Min`);
  }
  return next;
};

const enforceEmomGroup = (group) => {
  const children = group.children;
  if (children.some((c) => c?.type === "Group")) {
    debug.warn(
      `EMOM "${group.label}": nested Groups — window enforcement skipped`,
    );
    return;
  }
  const stations = children.filter((c) => c?.type === "Work");
  if (stations.length === 0) {
    debug.warn(`EMOM "${group.label}": no Work stations — nothing to enforce`);
    return;
  }

  // Every station becomes counted work, so finishing early actually earns rest.
  stations.forEach(countifyStation);

  const roundWork = () => stations.reduce((sum, s) => sum + stationSeconds(s), 0);

  const stated = labelDurationSeconds(group.label);
  const tokenWindow = emomTokenWindowS(group.label) ?? EMOM_WINDOW_S;
  let rounds = clampInt(Number(group.value) || 1, 1, EMOM_MAX_ROUNDS);
  let windowS;

  // Smallest whole-minute window the prescribed work actually fits.
  const fitWindowS = () =>
    Math.min(
      EMOM_MAX_WINDOW_S,
      Math.max(
        EMOM_WINDOW_S,
        Math.ceil(roundWork() / DENSITY_MAX / EMOM_WINDOW_S) * EMOM_WINDOW_S,
      ),
    );

  // Keep the block's total duration fixed while the window changes underneath
  // it: 5 × 2:00 and 10 × 1:00 are both the ten minutes the label promises.
  // With no duration in the label the token is the model's stated window — but
  // only where the work fits it. Seven rounds of five 50-second stations is not
  // an "EMOM" whatever the label says; there the work sets the window and the
  // prescribed round count stands.
  const totalS = stated ?? rounds * Math.max(tokenWindow, fitWindowS());
  const roundsFor = (w) => clampInt(totalS / w, 1, EMOM_MAX_ROUNDS);

  if (stations.length === 1) {
    // The token is the window; the round count follows the stated total.
    windowS = tokenWindow;
  } else {
    // Multi-station round: the window is however long one pass through the
    // stations is given, snapped to a whole number of minutes.
    windowS = clampInt(totalS / rounds / 60, 1, EMOM_MAX_WINDOW_S / 60) * 60;
  }
  rounds = roundsFor(windowS);

  // Fit the work to the window: trim first, widen the window only when the
  // rep/duration floors make trimming impossible.
  for (let i = 0; i < EMOM_FIT_ITERATIONS; i++) {
    if (roundWork() <= windowS - MIN_DURATION_S) break;
    trimStationsToFit(stations, windowS - MIN_DURATION_S);
    if (roundWork() <= windowS - MIN_DURATION_S) break;
    if (windowS >= EMOM_MAX_WINDOW_S) {
      debug.warn(
        `EMOM "${group.label}": ~${roundWork().toFixed(0)}s of work will not fit a ${windowS}s window`,
      );
      break;
    }
    windowS += EMOM_WINDOW_S;
    rounds = roundsFor(windowS);
  }

  // Tighten a window that is mostly idle — unless the block is heavy enough
  // that the long recovery is the prescription.
  const work = roundWork();
  if (work < DENSITY_MIN * windowS && !stations.some(isHeavyStation)) {
    const tightened = Math.max(
      EMOM_WINDOW_S,
      Math.ceil(work / DENSITY_MAX / EMOM_WINDOW_S) * EMOM_WINDOW_S,
    );
    if (tightened < windowS) {
      debug.warn(
        `EMOM "${group.label}": ${work.toFixed(0)}s of work in a ${windowS}s window ` +
          `(${Math.round((work / windowS) * 100)}%) — tightened to ${tightened}s`,
      );
      windowS = tightened;
      rounds = roundsFor(windowS);
    }
  }

  // The window is a self-paced countdown holding the stations as a checklist —
  // no Rest node: whatever is left of the clock is the rest.
  const nextLabel = emomLabel(group.label, windowS, rounds * windowS);
  debug.warn(
    `EMOM "${group.label}" → "${nextLabel}": ${rounds} × ${windowS}s window ` +
      `(~${roundWork().toFixed(0)}s of counted work per window)`,
  );
  group.value = rounds;
  group.label = nextLabel;
  group.children = [
    {
      id: `${group.id || "emom"}-window`,
      type: "Group",
      label: `${emomToken(windowS)} Window`,
      unit: "s",
      value: windowS,
      children: stations,
    },
  ];
};

// --- MISPLACED BLOCK HOISTING ------------------------------------------------
//
// A block that states its own duration cannot also be a component of another
// repeating block. "14-Min E2MOM" nested inside a Group that runs 8 rounds
// costs 112 minutes, not 14, and the label it carries becomes a lie. Models
// place the second block INSIDE the first one's rounds instead of beside it,
// and because rounds multiply, the error is catastrophic rather than untidy:
// one reported session promised a "16-Min E2MOM" and ran 134 minutes, on
// content that costs 31 as siblings.
//
// Nothing downstream catches it. enforceEmomGroup bails the moment it sees a
// Group child — it cannot tell a legitimate window from a misplaced block — and
// the timing scaler leaves protocol-labelled blocks alone by design, so the
// inflated total survives scaling and only surfaces as a timing complaint the
// refinement pass cannot act on.
//
// So the nesting is undone here, before either of those run. The label is
// authoritative, exactly as it already is for a time-capped Group's cap
// (pinLabelDuration) and for an EMOM's rounds × window (enforceEmomGroup): the
// nested block moves out to become the next sibling of the block that held it.
// Nothing prescribed is lost — only the multiplication goes away.
//
// Deliberately narrow. It fires only when the nested block's OWN label states a
// duration and the enclosing Group actually repeats, which is the case where the
// contradiction is arithmetic rather than a matter of taste. A Tabata labelled
// "Tabata" inside a 3-round circuit states no duration and is left alone.

const REPEAT_UNIT_SET = ["rounds", "sets", "workout"];

const isRepeatingGroup = (node) =>
  node?.type === "Group" &&
  REPEAT_UNIT_SET.includes(String(node.unit || "").toLowerCase());

const hoistMisplacedBlocks = (plan, onRepair) => {
  const report = typeof onRepair === "function" ? onRepair : () => {};

  const visit = (node) => {
    if (!Array.isArray(node?.children)) return;
    // Depth-first, so a chain of misplaced blocks unwinds from the inside out.
    node.children.forEach(visit);

    const out = [];
    node.children.forEach((child) => {
      const rounds = Number(child?.value) || 1;
      if (!isRepeatingGroup(child) || rounds <= 1) {
        out.push(child);
        return;
      }
      const kids = Array.isArray(child.children) ? child.children : [];
      const misplaced = kids.filter(
        (kid) =>
          isRepeatingGroup(kid) && labelDurationSeconds(kid.label) != null,
      );
      if (misplaced.length === 0) {
        out.push(child);
        return;
      }
      const kept = kids.filter((kid) => !misplaced.includes(kid));
      misplaced.forEach(() => report("repair.misplacedBlock"));
      debug.warn(
        `Hoisted ${misplaced.length} misplaced block(s) out of "${child.label}" (${rounds} rounds)`,
      );
      // A Group whose only content was the misplaced block is not a block at
      // all — dropping it beats leaving one that runs N rounds of nothing.
      if (kept.length > 0) {
        child.children = kept;
        out.push(child);
      }
      out.push(...misplaced);
    });
    node.children = out;
  };

  visit(plan);
};

const enforceEmomWindows = (plan) => {
  const visit = (node) => {
    if (!node || typeof node !== "object") return;
    if (
      node.type === "Group" &&
      !isTimedUnit(node.unit) &&
      EMOM_TOKEN_RE.test(String(node.label || "")) &&
      Array.isArray(node.children) &&
      node.children.length > 0
    ) {
      enforceEmomGroup(node);
    }
    // Still recurse: a bailed-out block (nested Groups) may hold its own EMOM.
    if (Array.isArray(node.children)) node.children.forEach(visit);
  };
  visit(plan);
};

export {
  validateAndAdjustTiming,
  normalizeUnits,
  enforceEmomWindows,
  hoistMisplacedBlocks,
  // Shared with validatePlan so the "label must match the arithmetic" check
  // reads a block's promise exactly the way the repairs here do.
  labelDurationSeconds,
};
